/**
 * CapacityHeatmapCell.tsx
 * A single weekly capacity cell for the Employee Schedule grid.
 *
 * Wave 3 (percent retirement, Q5B): the number is the PERCENT-OF-WEEK busy —
 * busyDays ÷ workingDays (holiday-aware), e.g. "60%" for 3 of 5 busy days,
 * "75%" for 3 busy days in a 4-working-day holiday week. The additive
 * busyDays/workingDays wire fields are preferred; the legacy-scale sum ÷
 * (workingDays × 100) is the fallback (identical except on conflict days).
 *
 * The red ring + ⚠ is now the ONE remaining hard signal: a task assigned on a
 * day the person has a dated absence (`hasOverbook`, Q3B — legacy field name).
 * An amber ring flags the NON-blocking weekly overload advisory (more
 * task-days than the week holds — Q1B family). Task stacking alone never
 * paints red.
 *
 * Color semantics live as tokens under theme.schedule.* + the shared heatmap helper.
 *
 * Extension surface for Phase 3 items 3–9:
 *   - onClick  → Phase 3 item 4 (click cell → popup)
 *   - isExpanded → Phase 3 item 3 (expand row)
 */

import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Warning } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { HEATMAP_LOW_MAX, HEATMAP_MID_MAX } from '../../constants/scheduleCapacity';
import { capacityHeatColor } from '../../utils/scheduleCapacity';
import {
  weekPercentFromCell,
  busyDayFractionLabel,
} from '../../utils/personDayDisplay';

/** A full working week's legacy-scale ceiling: 5 days × 100 (fallback divisor). */
const FULL_WEEK_CAPACITY_PCT = 500;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CapacityHeatmapCellProps {
  /**
   * LEGACY-scale weekly sum (busy day = 100, conflict day = 200). Used only as
   * the fallback when the additive busyDays/workingDays are absent.
   */
  totalPercent: number;
  /**
   * Wave 3 (Q3B): true when a task lands on a day the person has a dated
   * absence — the one remaining hard conflict (red ring + ⚠).
   */
  hasOverbook: boolean;
  /** Wave-3 additive: count of busy working days (Q5B numerator). */
  busyDays?: number;
  /** Wave-3 additive: working days in the week (holiday-aware denominator). */
  workingDays?: number;
  /**
   * Non-blocking weekly overload advisory: the person carries more task-days
   * than the week holds (Σ task-days > working days). Amber ring, never red.
   */
  overloaded?: boolean;
  /**
   * LEGACY fallback divisor — the week's ceiling in legacy scale (working days
   * × 100). Only consulted when busyDays/workingDays are absent (P-22 wiring).
   */
  weekCapacityPercent?: number;
  /**
   * Called when the user clicks the cell.
   * Phase 3 item 4: receives the cell's DOM element to anchor the popover.
   */
  onClick?: (anchorEl: HTMLElement) => void;
  /** Accessibility label — e.g. "Michael C., Week of Oct 15" */
  ariaLabel?: string;
}

// ---------------------------------------------------------------------------
// Pure helper — pick the heatmap background token based on percent-of-week
// ---------------------------------------------------------------------------

/**
 * Return the name of the theme.schedule token for a given weekly percent-of-week.
 *
 * Bands (% of the week's working days busy):
 *   ≤ 0      → noActivity
 *   1 – 70   → heatmapLow  (green)
 *   71 – 100 → heatmapMid  (yellow)
 *   > 100    → heatmapHigh (red — legacy data only; the binary model caps at 100)
 *
 * NOTE: the absence-day conflict is a separate signal — the component forces
 * the red treatment when `hasOverbook` is true regardless of the band.
 *
 * This function is the canonical heatmap-color picker used in tests
 * (see phase3-item2-employee-weeks.test.ts).
 */
export function heatmapColorToken(
  pct: number,
): 'noActivity' | 'heatmapLow' | 'heatmapMid' | 'heatmapHigh' {
  if (pct <= 0) return 'noActivity';
  if (pct <= HEATMAP_LOW_MAX) return 'heatmapLow';
  if (pct <= HEATMAP_MID_MAX) return 'heatmapMid';
  return 'heatmapHigh';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CapacityHeatmapCell: React.FC<CapacityHeatmapCellProps> = ({
  totalPercent,
  hasOverbook,
  busyDays,
  workingDays,
  overloaded = false,
  weekCapacityPercent = FULL_WEEK_CAPACITY_PCT,
  onClick,
  ariaLabel,
}) => {
  const theme = useTheme();

  // Q5B percent-of-week: additive truth preferred; legacy sum ÷ workingDays
  // as fallback (weekCapacityPercent = workingDays × 100 from the caller).
  const cap = weekCapacityPercent > 0 ? weekCapacityPercent : FULL_WEEK_CAPACITY_PCT;
  const utilRounded = weekPercentFromCell(
    { totalPercent, busyDays, workingDays },
    cap / 100,
  );
  const isEmpty = utilRounded <= 0 && !hasOverbook;
  const { bg, fg } = capacityHeatColor(Math.min(utilRounded, 100));

  // The one hard signal (Q3B): task on a day off → red ring + ⚠. The weekly
  // overload advisory (more task-days than the week holds) is amber, non-blocking.
  const conflict = hasOverbook;
  const fraction = busyDayFractionLabel({ busyDays, workingDays });

  const tooltipText = conflict
    ? `Conflict — assigned on a day off.${fraction ? ` ${utilRounded}% of the week busy — ${fraction}.` : ''}`
    : isEmpty
      ? 'No assignments this week'
      : `${utilRounded}% of the week busy${fraction ? ` — ${fraction}` : ''}${
          overloaded ? ' · more task-days than the week holds' : ''
        }`;

  const conflictRing = theme.schedule.heatmapHigh;
  const overloadRing = theme.schedule.maintenance;

  return (
    <Tooltip title={tooltipText} arrow placement="top" enterDelay={600} enterNextDelay={600}>
      <Box
        role="button"
        tabIndex={onClick ? 0 : -1}
        aria-label={ariaLabel}
        onClick={(e) => {
          if (onClick) onClick(e.currentTarget as HTMLElement);
        }}
        onKeyDown={(e) => {
          if (onClick && (e.key === 'Enter' || e.key === ' ')) {
            onClick(e.currentTarget as HTMLElement);
          }
        }}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 52,
          minWidth: 90,
          borderRadius: '4px',
          backgroundColor: isEmpty ? 'transparent' : bg,
          border: `1px solid ${
            isEmpty ? alpha(theme.schedule.noActivity, 0.5) : alpha(theme.schedule.committed, 0.2)
          }`,
          // Conflict (red) outranks the overload advisory (amber).
          boxShadow: conflict
            ? `inset 0 0 0 2px ${conflictRing}`
            : overloaded
              ? `inset 0 0 0 2px ${overloadRing}`
              : 'none',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'filter 0.15s ease, background-color 0.15s ease',
          '&:hover': onClick ? { filter: 'brightness(0.95)' } : undefined,
        }}
      >
        {/* Percent-of-week label (Q5B): 100% = every working day busy. */}
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            fontSize: '0.78rem',
            color: isEmpty ? theme.palette.text.disabled : fg,
            lineHeight: 1.2,
          }}
        >
          {isEmpty ? '—' : `${utilRounded}%`}
        </Typography>

        {/* Fill bar toward a fully-busy week. */}
        {!isEmpty && (
          <Box
            sx={{
              mt: 0.5,
              height: 4,
              width: `${Math.min(utilRounded, 100)}%`,
              maxWidth: '80%',
              minWidth: 8,
              borderRadius: '2px',
              backgroundColor: conflict ? conflictRing : fg,
            }}
            aria-hidden="true"
          />
        )}

        {/* Warning icon — conflict (red) or weekly overload advisory (amber). */}
        {(conflict || overloaded) && (
          <Box
            sx={{
              position: 'absolute',
              top: 3,
              right: 3,
              lineHeight: 0,
            }}
          >
            <Warning
              sx={{
                fontSize: '0.9rem',
                color: conflict ? conflictRing : overloadRing,
              }}
              aria-label={conflict ? 'Assigned on a day off' : 'More task-days than the week holds'}
            />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};
